import React, { ChangeEvent, FocusEventHandler, MouseEvent, useEffect, useState } from 'react';
import {
  IconButton,
  IconButtonProps,
  Paper,
  Popover,
  PopoverProps,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import { format as formatDate } from 'date-fns';
import { DatePicker, DatePickerProps, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { InsertInvitation } from '@material-ui/icons';
import { isValidDate, parseDate } from '../utils';
import DateFnsUtils from '@date-io/date-fns';

export interface DateFieldChangeDetail {
  reason: 'input' | 'calendarSelect';
}

export type DateFieldProps = Omit<TextFieldProps, 'onChange'> & {
  format?: string;
  helperText?: string;
  value?: Date | null;
  onChange?: (date: Date | null, detail: DateFieldChangeDetail) => void;
  DatePickerProps?: Partial<DatePickerProps>;
  PopoverProps?: Partial<PopoverProps>;
  KeyboardButtonProps?: IconButtonProps;
  setToNoon?: boolean;
};

export const DateField = ({
  format = 'MM/dd/yyyy',
  helperText = 'mm/dd/yyyy',
  value = null,
  onChange = () => undefined,
  onBlur = () => undefined,
  onFocus = () => undefined,
  DatePickerProps = {},
  PopoverProps = {},
  KeyboardButtonProps = {},
  InputProps,
  setToNoon = true,
  ...props
}: DateFieldProps): JSX.Element => {
  const [inputValue, setInputValue] = useState(value && isValidDate(value) ? formatDate(value, format) : '');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const newDate = new Date(value || '');
    if (isFocused && !props.autoFocus) return;
    if (!inputValue && value) {
      setInputValue(isValidDate(newDate) ? formatDate(newDate, format) : '');
    } else if (!value && !isFocused) {
      setInputValue('');
    } else if (isValidDate(newDate)) {
      setInputValue(formatDate(newDate, format));
    }
  }, [value, isFocused]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    const parsedDate = parseDate(event.target.value, format, setToNoon);
    onChange(parsedDate, { reason: 'input' });
  };

  const handlePickerChange = (date: Date | null) => {
    let currentDate = date ? new Date(date.getTime()) : new Date();

    currentDate = setToNoon
      ? new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 12, 0, 0, 0))
      : currentDate;

    setInputValue(formatDate(currentDate, format));
    onChange(currentDate, { reason: 'calendarSelect' });
    handlePickerClose();
  };

  const handlePickerClose = () => {
    setAnchorEl(null);
  };

  const handlePickerOpen = (event: MouseEvent<HTMLButtonElement>) => {
    !anchorEl && setAnchorEl(event.currentTarget);
  };

  const handleFocus: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    onFocus(event);
    setIsFocused(true);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    setIsFocused(false);
    if (value && isValidDate(value)) {
      onBlur(event);
      setInputValue(formatDate(value, format));
    } else {
      setInputValue('');
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <>
        <TextField
          autoComplete="off"
          data-testid="datefield-input"
          value={inputValue}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder="mm/dd/yyyy"
          helperText={helperText}
          {...props}
          InputProps={{
            endAdornment: (
              <>
                {InputProps?.endAdornment}
                {!props.disabled && (
                  <IconButton color="primary" size="small" onClick={handlePickerOpen} {...KeyboardButtonProps}>
                    <InsertInvitation fontSize="small" data-testid="datefield-calendar-button" />
                  </IconButton>
                )}
              </>
            ),
          }}
        />
        {!!anchorEl && (
          <Popover
            open
            anchorEl={anchorEl}
            onClose={handlePickerClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            data-testid="datefield-calendar-popover"
            {...PopoverProps}
          >
            <Paper>
              <DatePicker
                allowKeyboardControl
                autoFocus
                value={value}
                variant="static"
                minDate={new Date(1000, 0)}
                {...DatePickerProps}
                onChange={handlePickerChange}
                onBlur={handleBlur}
                initialFocusedDate={new Date()}
              />
            </Paper>
          </Popover>
        )}
      </>
    </MuiPickersUtilsProvider>
  );
};

export default DateField;
